import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { RefObject } from "react";

/**
 * Interface representing an SSML tag containing the selection
 */
export interface SelectionTagInfo {
  tagType: string; // The type of SSML tag (e.g., "voice", "emphasis")
  tagValue: string; // The value of the tag
  element: Element; // The DOM element for the tag
  level: number; // Hierarchy level (0 is innermost)
}

/**
 * Checks if the user has selected multiple characters and identifies all SSML tags
 * containing the selection along with their hierarchy
 *
 * @param editorRef - React ref to the TinyMCE editor
 * @returns Object with selection information
 */
export const getSelectionTagHierarchy = (
  editorRef: RefObject<EditorRef | null>
): {
  hasMultipleChars: boolean;
  tagHierarchy: SelectionTagInfo[];
} => {
  const editor = editorRef.current?.editor;
  const result = {
    hasMultipleChars: false,
    tagHierarchy: [] as SelectionTagInfo[],
  };

  if (!editor) return result;

  // Get the selected content as text (strips HTML tags)
  const selectedContent = editor.selection?.getContent({ format: "text" });

  // Check if multiple characters are selected
  result.hasMultipleChars = selectedContent?.trim().length >= 1;

  // If no multiple characters are selected, return early
  if (!result.hasMultipleChars) return result;

  // Get the selected node and find all parent elements with data-ssml attribute
  let currentElement: Element | null = editor.selection.getNode();
  let level = 0;

  while (currentElement) {
    if (currentElement.nodeType === Node.ELEMENT_NODE) {
      const dataSsml = currentElement.getAttribute("data-ssml");

      if (dataSsml) {
        //Use robust splitting instead of strict regex to handle ID formats
        const tagType = dataSsml.split("-")[0];
        const tagValue = dataSsml.includes('=') ? dataSsml.split('=')[1] : '';

        result.tagHierarchy.push({
          tagType,
          tagValue,
          element: currentElement,
          level,
        });

        level++;
      }
    }

    currentElement = currentElement.parentElement;
  }

  return result;
};


/**
 * Helper function to get detailed information about the tag hierarchy for debugging
 */
export const getTagHierarchyDebugInfo = (
  tagHierarchy: SelectionTagInfo[]
): string => {
  if (tagHierarchy.length === 0) return "No SSML tags";

  return tagHierarchy
    .map((tag, index) => {
      const levelLabel =
        index === 0
          ? "innermost"
          : index === tagHierarchy.length - 1
            ? "outermost"
            : `level ${tag.level}`;

      return `${tag.tagType} (${tag.tagValue}) - ${levelLabel}`;
    })
    .join("\n");
};
